import React, { useState, useEffect } from 'react';
import type { GetServerSidePropsContext, NextPage } from 'next';
import styles from './index.module.scss';
import EfButton from '@elem/ui/ef-button';
import { TimeIcon, XIcon, Act1Icon, Act2Icon, Act3Icon } from '../icons';
import EcoImg from 'eco-design/base/eco-img';
import LuckyTurntable from '@/components/EcoCredits/LuckyTurntable';
import LuckyHistory from '@/components/EcoCredits/LuckyTurntable/History';
import FullScreenModal from '@/components/EcoCredits/FullScreenModal';
import { useProductStore } from '../../../ProductProvider';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useGlobal } from '@/utils/useGlobal';
import { Navigation, Scrollbar } from 'swiper';
import { useCountDown } from 'ahooks';
import { IPageStatus } from '../../../config/IPageStatus';
import { useLang } from '@/i18n';
import gtm from '@/utils/gtm';

const ActivityIntroduction: React.FC = () => {
    const { isLogin, pageStatus, timeLine } = useProductStore();
    const { getSystemTime } = useGlobal();
    const [slidesOffset, setSlidesOffset] = useState<undefined | number>(undefined);
    const { t } = useLang('Delta3MaxSeriesPreheat.activity_introduction');
    const pageCountDownStatus = pageStatus === IPageStatus.statusA ? IPageStatus.statusB : IPageStatus.statusD;
    const [showPage, setShowPage] = useState('basic'); // basic / history_modal / coupon_modal / points_modal / bouns_modal / qhistory_modal / lucky_history
    const [, formattedRes] = useCountDown({
        // leftTime: now - Number(startTime),
        leftTime: timeLine[pageCountDownStatus].start - getSystemTime(),
    });
    const { days, hours, minutes, seconds } = formattedRes;

    const handleCompletion = (val: number) => (val < 10 ? `0${val}` : val);

    const getShareLink = () => {
        const statusALink = `https://x.com/EcoFlowJapan`;

        const shareLink = `https://twitter.com/intent/tweet?text=${encodeURIComponent(t('share_text'))}`;
        if (pageStatus === IPageStatus.statusA || pageStatus === IPageStatus.statusB) {
            return statusALink;
        }
        return shareLink;
    };

    const items = [
        {
            img: <Act1Icon />,
            title: t('item1_title'),
            time: t.rich('item1_time', {
                text1: (str: string) => <span>{str}</span>,
            }),
            desc: t.rich('item1_desc', {
                text1: (str: string) => <span>{str}</span>,
            }),
            cta: t('item1_cta'),
            url: '#luck-turn-table',
        },
        {
            img: <Act2Icon />,
            title: t('item2_title'),
            time: t.rich('item2_time', {
                text1: (str: string) => <span>{str}</span>,
            }),
            desc: t.rich('item2_desc', {
                text1: (str: string) => <span>{str}</span>,
            }),
            cta: t('item2_cta'),
            url: '#d3mProductList',
        },
        {
            img: <Act3Icon />,
            title: pageStatus === IPageStatus.statusA || pageStatus === IPageStatus.statusB ? t('item3_title') : t('item3_title2'),
            time:
                pageStatus === IPageStatus.statusA || pageStatus === IPageStatus.statusB
                    ? t.rich('item3_time', {
                          text1: (str: string) => <span>{str}</span>,
                      })
                    : t.rich('item3_time2', {
                          text1: (str: string) => <span>{str}</span>,
                      }),
            desc: pageStatus === IPageStatus.statusA || pageStatus === IPageStatus.statusB ? t('item3_desc') : t('item3_desc2'),
            cta: pageStatus === IPageStatus.statusA || pageStatus === IPageStatus.statusB ? t('item3_cta') : t('item3_cta2'),
            url: getShareLink(),
        },
    ];

    const handleGtm = (index: number) => {
        if (index === 2) {
            gtm.push({
                event: 'uaEvent',
                event_name: 'activity_join_social',
                method: 'X',
                position: t('title'),
                button_name: items[2].cta,
            });
            return;
        }
        gtm.push({
            event: 'uaEvent',
            event_name: 'click_anchor',
            position: items[index]?.url?.split('#')?.[1],
            button_name: items[index]?.cta,
        });
    };

    const gtmActiveFun = () => {
        gtm.push({
            event: 'uaEvent',
            event_name: 'activity_button',
            list_name: '会員限定特典',
            button_name: 'go',
            function: 'Lucky Spin',
        });
    };

    useEffect(() => {
        if (typeof window !== 'undefined') {
            if (window.innerWidth < 768) {
                setSlidesOffset(24);
            } else if (window.innerWidth < 1024) {
                setSlidesOffset(48);
            } else {
                setSlidesOffset(undefined);
            }
        }
    }, []);

    return (
        <section className={styles.ActivityIntroduction}>
            <div className="title-box">
                <div className="title1">{t('title')}</div>
                <div className="preheat-line">
                    <span>
                        <TimeIcon />
                    </span>
                    <span>{t('preheat_desc')}</span>
                </div>
                {pageStatus === IPageStatus.statusD ? null : (
                    <div className="countdown-box">
                        <div className="countdown-label">{pageCountDownStatus === IPageStatus.statusD ? t('time_label2') : t('time_label')}</div>
                        <div className="countdown-content">
                            <span className="num">{handleCompletion(days)}</span>
                            <span className="unit">{t('day')}</span>
                            <span className="num">{handleCompletion(hours)}</span>
                            <span className="unit">{t('hour')}</span>
                            <span className="num">{handleCompletion(minutes)}</span>
                            <span className="unit">{t('min')}</span>
                            <span className="num">{handleCompletion(seconds)}</span>
                            <span className="unit">{t('sec')}</span>
                        </div>
                    </div>
                )}
            </div>
            <div className="activity-content">
                <Swiper spaceBetween={20} slidesPerView={'auto'} slidesOffsetBefore={slidesOffset} slidesOffsetAfter={slidesOffset}>
                    {items.map((item, index) => (
                        <SwiperSlide key={index} className="slide-item">
                            <div className="item">
                                <div className="item-des-content">
                                    <div className="icon">{item.img}</div>
                                    <div className="item-title">{item.title}</div>
                                    <div className="item-time">{item.time}</div>
                                    <div className="item-desc">{item.desc}</div>
                                </div>
                                <EfButton
                                    type="buy_now"
                                    className="introduce-btn"
                                    onClick={() => handleGtm(index)}
                                    href={item.url}
                                    target={!item.url.startsWith('#') ? '_blank' : ''}
                                    rel={!item.url.startsWith('#') ? 'noopener noreferrer' : ''}
                                >
                                    {index === 2 && <XIcon />} <span>{item.cta}</span>
                                </EfButton>
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
            <div className="luck-turntable-box" id="luck-turn-table">
                <LuckyTurntable isLogin={isLogin} setShowPage={setShowPage} classNameStr="d3m-turntable" gtmActiveFun={gtmActiveFun} />
            </div>
            {showPage === 'lucky_history' && (
                <FullScreenModal visible={showPage === 'lucky_history'} className={showPage} closeModal={() => setShowPage('basic')}>
                    <LuckyHistory showPage={showPage} />
                </FullScreenModal>
            )}
        </section>
    );
};

export default ActivityIntroduction;
